import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveParticipantSession } from '@services/airtable';

type NotificationType = 'success' | 'error';

export default function SessionCollectorPage() {
  const [teamName, setTeamName] = useState('');
  const [lines, setLines] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    type: NotificationType;
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const showNotification = (type: NotificationType, message: string) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification((n) => ({ ...n, show: false })), 4000);
  };

  const fullPrompt = lines.filter((l) => l.trim()).join('\n');

  const handleCopyPrompt = async () => {
    if (!fullPrompt) return;
    await navigator.clipboard.writeText(fullPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLineChange = (idx: number, value: string) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? value : l)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, '']);
  };

  const removeLine = (idx: number) => {
    if (lines.length === 1) return;
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!teamName.trim()) {
      showNotification('error', 'Ingresa el nombre del equipo.');
      return;
    }
    if (!fullPrompt) {
      showNotification('error', 'Ingresa al menos una línea de prompt.');
      return;
    }

    setLoading(true);
    try {
      await saveParticipantSession({
        teamName: teamName.trim(),
        prompt: fullPrompt
      });
      showNotification('success', '¡Sesión guardada correctamente!');
      setTeamName('');
      setLines(['']);
    } catch (err) {
      console.error(err);
      showNotification('error', 'Error al guardar. Revisa la consola.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 dark:bg-black-100">
      {/* Notification */}
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, y: -40, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -40, x: '-50%' }}
            className="fixed top-6 left-1/2 z-50 px-6 py-4 rounded-xl border shadow-2xl backdrop-blur-md"
            style={{
              background: notification.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              borderColor: notification.type === 'success' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'
            }}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{notification.type === 'success' ? '✅' : '❌'}</span>
              <p
                className={`text-sm font-medium ${notification.type === 'success' ? 'text-green-300' : 'text-red-300'}`}
              >
                {notification.message}
              </p>
              <button
                onClick={() => setNotification((n) => ({ ...n, show: false }))}
                className="ml-4 text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-4xl md:text-5xl font-bold text-white"
          >
            Session Collector
          </motion.h2>
          <p className="text-gray-400 mt-3 text-sm md:text-base">
            Construye tu prompt línea a línea y envíalo con tu equipo.
          </p>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm space-y-6"
        >
          {/* TeamName */}
          <div>
            <label htmlFor="team-name" className="block text-sm text-gray-400 mb-1">
              Team Name
            </label>
            <input
              id="team-name"
              type="text"
              placeholder="Nombre del equipo"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none transition-colors"
            />
          </div>

          {/* Prompt Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span id="prompt-label" className="block text-sm text-gray-400">
                Prompt (línea por línea)
              </span>
              <button
                type="button"
                onClick={handleCopyPrompt}
                className={`px-3 py-1 text-xs rounded-lg border transition-all ${
                  copied
                    ? 'bg-green-500/20 border-green-500/40 text-green-300'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                {copied ? '✓ Copiado' : '📄 Copiar todo'}
              </button>
            </div>

            <div className="space-y-2">
              {lines.map((line, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2"
                >
                  <span className="text-xs text-gray-600 w-6 text-right shrink-0">{idx + 1}.</span>
                  <input
                    type="text"
                    aria-labelledby="prompt-label"
                    placeholder={`Línea ${idx + 1}...`}
                    value={line}
                    onChange={(e) => handleLineChange(idx, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addLine();
                      }
                    }}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none transition-colors text-sm"
                  />
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      className="text-gray-600 hover:text-red-400 transition-colors text-lg shrink-0"
                    >
                      ×
                    </button>
                  )}
                </motion.div>
              ))}
            </div>

            <button
              type="button"
              onClick={addLine}
              className="mt-3 px-4 py-2 text-xs rounded-xl bg-white/5 border border-dashed border-white/20 text-gray-400 hover:bg-white/10 hover:text-white transition-all w-full"
            >
              + Agregar línea
            </button>
          </div>

          {/* Preview */}
          {fullPrompt && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Vista previa:</p>
              <pre className="text-sm text-gray-300 whitespace-pre-wrap bg-white/[0.02] p-4 rounded-xl border border-white/5 max-h-[150px] overflow-y-auto">
                {fullPrompt}
              </pre>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full px-6 py-4 rounded-xl font-bold text-sm transition-all ${
              loading
                ? 'bg-indigo-500/20 text-indigo-300 cursor-wait'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 hover:shadow-lg hover:shadow-indigo-500/25'
            }`}
          >
            {loading ? '⏳ Guardando...' : 'Enviar Sesión'}
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
